import React, { useEffect, useRef, useState } from 'react';
import { useConfirm } from '../context/ConfirmContext.jsx';
import { humanizeKey } from '../utils/fileFacts';
import '../css/MetadataEditor.css';

// Centralized Schema
// `projects` is deliberately absent: it was a free-text list, retired when
// projects became real entities (D1). Membership now lives on
// `metadata.projectIds` and is edited through the Projects section of the file
// details modal, which can add and remove against the real API. Re-adding a
// text field here would give two answers to one question, and only one of them
// would move files.
export const METADATA_SCHEMA = {
    name: { type: 'string', label: 'File name', required: true },
    authors: { type: 'string[]', label: 'Authors', placeholder: 'Studio, artist or creator' },
    sources: { type: 'string[]', label: 'Sources', placeholder: 'Where it came from (URL or note)' },
    fileGroups: { type: 'string[]', label: 'File groups', placeholder: 'Group name' },
    license: { type: 'string', label: 'License', options: ['Owned', 'CC0', 'CC BY', 'CC BY-NC', 'CC BY-NC-SA', 'CC BY-ND', 'CC BY-SA'] },
};

const Pencil = (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" /><path d="m14.5 6.5 3 3" />
    </svg>
);

const Cross = (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" {...props}>
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const isBlank = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * MetadataEditor — the editable half of a file's record.
 *
 * Rows are label + value with an always-present (but quiet) edit affordance.
 * The previous table revealed its pencil only on :hover, which meant the fields
 * were undiscoverable on touch and invisible to anyone scanning for "can I
 * change this?" — and every empty field still rendered a full row saying
 * "Empty", so a mostly-blank record was mostly noise. Empty fields are now the
 * invitation to fill them.
 *
 * Props (all optional except the handlers):
 *   metadata      – the document's metadata object
 *   schema        – { key: { type, label, options, placeholder, required } }
 *   readOnlyKeys  – rendered as plain rows, not editable
 *   hiddenKeys    – not rendered at all (owned by some other control on screen)
 *   readOnlyLabel – heading above the read-only block
 *   onSave        – (key, value) => Promise
 *   onRemove      – (key) => Promise; enables deleting custom properties
 *   disabled      – read-only mode for the whole editor
 */
const MetadataEditor = ({
    metadata = {},
    schema = METADATA_SCHEMA,
    readOnlyKeys = [],
    hiddenKeys = [],
    readOnlyLabel = null,
    onSave,
    onRemove,
    disabled = false,
}) => {
    const confirm = useConfirm();
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [validationError, setValidationError] = useState(null);

    // Custom key state
    const [isAddingCustom, setIsAddingCustom] = useState(false);
    const [customKeyName, setCustomKeyName] = useState('');

    const lastListInputRef = useRef(null);
    const focusNewListItem = useRef(false);

    // Adding a list row should put the caret in it — otherwise every new author
    // costs a click on the row you just created.
    useEffect(() => {
        if (!focusNewListItem.current) return;
        focusNewListItem.current = false;
        lastListInputRef.current?.focus();
    }, [editValue]);

    const startEditing = (key, value, fieldSchema) => {
        let initialValue;
        if (fieldSchema.type.endsWith('[]')) {
            // L-16: wrap list entries with a stable id so React keys survive
            // reorder/remove (index keys caused focus loss and wrong-row edits).
            const entries = (Array.isArray(value) ? value : []).map((v) => ({ id: crypto.randomUUID(), value: v }));
            // An empty list opens with one blank row: the first thing anyone does
            // after clicking "Add authors" is add an author.
            initialValue = entries.length ? entries : [{ id: crypto.randomUUID(), value: '' }];
        } else {
            initialValue = value || '';
        }
        setValidationError(null);
        setEditingField(key);
        setEditValue(initialValue);
    };

    const cancelEditing = () => {
        setEditingField(null);
        setEditValue(null);
        setValidationError(null);
        setIsAddingCustom(false);
        setCustomKeyName('');
    };

    const handleSave = async (key, fieldSchema) => {
        let finalValue = editValue;

        if (fieldSchema.type.endsWith('[]')) {
            finalValue = finalValue.map((entry) => String(entry.value).trim()).filter(Boolean);
            if (fieldSchema.type === 'number[]') finalValue = finalValue.map(Number).filter((n) => !isNaN(n));
        } else if (fieldSchema.type === 'number') {
            finalValue = Number(finalValue);
        } else {
            finalValue = String(finalValue ?? '').trim();
        }

        // A blanked-out required field (a file with no name) is a worse outcome
        // than a rejected save, so it's caught here rather than sent.
        if (fieldSchema.required && isBlank(finalValue)) {
            setValidationError(`${fieldSchema.label || humanizeKey(key)} can’t be empty.`);
            return;
        }

        setIsLoading(true);
        setValidationError(null);
        try {
            await onSave(key, finalValue);
            cancelEditing();
        } catch (error) {
            // Surfaced globally by the axios interceptor (notification center).
            console.error('Failed to save metadata:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemove = async (key) => {
        const ok = await confirm({
            title: 'Remove property?',
            message: `Remove property "${key}"? Its value is deleted from this file.`,
            confirmText: 'Remove',
            cancelText: 'Cancel',
            danger: true,
        });
        if (ok) {
            setIsLoading(true);
            try {
                if (onRemove) await onRemove(key);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const startCustomField = () => {
        if (!customKeyName.trim()) return;
        const key = customKeyName.trim().replace(/\s+/g, '_'); // Basic sanitization
        startEditing(key, '', { type: 'string', label: humanizeKey(key) });
        setIsAddingCustom(false);
        setCustomKeyName('');
    };

    // --- List Editor Helpers ---
    const updateListItem = (index, value) => {
        setEditValue((prev) => prev.map((entry, i) => (i === index ? { ...entry, value } : entry)));
    };
    const addListItem = () => {
        focusNewListItem.current = true;
        setEditValue((prev) => [...prev, { id: crypto.randomUUID(), value: '' }]);
    };
    const removeListItem = (index) => setEditValue((prev) => prev.filter((_, i) => i !== index));
    const moveListItem = (index, dir) => {
        const newList = [...editValue];
        if (dir === -1 && index > 0) [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
        else if (dir === 1 && index < newList.length - 1) [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
        setEditValue(newList);
    };

    const renderListEditor = (key, fieldSchema) => {
        const list = Array.isArray(editValue) ? editValue : [];
        return (
            <div className="me-list-editor">
                {list.map((entry, index) => (
                    <div key={entry.id} className="me-list-item-row">
                        <input
                            type={fieldSchema.type === 'number[]' ? 'number' : 'text'}
                            value={entry.value}
                            onChange={(e) => updateListItem(index, e.target.value)}
                            onKeyDown={(e) => {
                                // Enter on the last row adds another; anywhere else
                                // it commits, which is what a one-item edit expects.
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (index === list.length - 1 && String(entry.value).trim()) addListItem();
                                    else handleSave(key, fieldSchema);
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    cancelEditing();
                                }
                            }}
                            ref={index === list.length - 1 ? lastListInputRef : undefined}
                            autoFocus={index === 0 && list.length === 1}
                            placeholder={fieldSchema.placeholder || 'Enter a value…'}
                            aria-label={`${fieldSchema.label || key} item ${index + 1}`}
                            disabled={disabled || isLoading}
                        />
                        <div className="me-list-item-actions">
                            <button type="button" title="Move up" aria-label="Move up"
                                    onClick={() => moveListItem(index, -1)}
                                    disabled={index === 0 || disabled || isLoading}>↑</button>
                            <button type="button" title="Move down" aria-label="Move down"
                                    onClick={() => moveListItem(index, 1)}
                                    disabled={index === list.length - 1 || disabled || isLoading}>↓</button>
                            <button type="button" title="Remove" aria-label="Remove item" className="me-remove-btn"
                                    onClick={() => removeListItem(index)}
                                    disabled={disabled || isLoading}><Cross /></button>
                        </div>
                    </div>
                ))}
                <button type="button" className="me-add-list-item-btn" onClick={addListItem} disabled={disabled || isLoading}>
                    + Add item
                </button>
            </div>
        );
    };

    const linkToLabel = (v) => {
        // fe-modals-meta-4: guard non-strings (would throw on .substring) and
        // URLs without a "://" scheme (indexOf → -1 previously dropped 2 chars).
        const s = String(v ?? '');
        const idx = s.indexOf('://');
        const str = idx >= 0 ? s.substring(idx + 3) : s;
        const n = Math.max(str.indexOf('/') + 2, 30);
        return str.length > n ? `${str.slice(0, n - 1)}...` : str;
    };

    const renderValue = (val) => {
        if (Array.isArray(val)) {
            return (
                <ul className="me-list-display">
                    {val.map((v, i) => (
                        <li key={i} className="me-value-chip">
                            {typeof v === 'string' && v.startsWith('http') ? (
                                <a target="_blank" rel="noopener noreferrer" href={v} className="me-link" title={v}>
                                    {linkToLabel(v)}
                                </a>
                            ) : (
                                v
                            )}
                        </li>
                    ))}
                </ul>
            );
        }
        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
        if (typeof val === 'string' && val.startsWith('http')) {
            return (
                <a target="_blank" rel="noopener noreferrer" href={val} className="me-link" title={val}>
                    {linkToLabel(val)}
                </a>
            );
        }
        return String(val);
    };

    const hidden = new Set(hiddenKeys);
    const readOnly = new Set(readOnlyKeys);
    const editableKeys = Array.from(new Set([...Object.keys(schema), ...Object.keys(metadata)]))
        .filter((k) => !readOnly.has(k) && !hidden.has(k));
    const readOnlyEntries = Object.entries(metadata).filter(([k]) => readOnly.has(k) && !hidden.has(k));

    return (
        <div className={`metadata-editor-component ${disabled ? 'disabled' : ''}`}>
            <dl className="me-fields">
                {editableKeys.map((key) => {
                    const fieldSchema = schema[key] || { type: 'string', label: humanizeKey(key) };
                    const label = fieldSchema.label || humanizeKey(key);
                    const isEditing = editingField === key;
                    const val = metadata[key];
                    const isCustom = !schema[key];
                    const empty = isBlank(val);

                    return (
                        <div key={key} className={`me-field ${isEditing ? 'is-editing' : ''} ${empty ? 'is-empty' : ''}`.trim()}>
                            <dt className="me-key">{label}</dt>
                            <dd className="me-val">
                                {isEditing ? (
                                    <div className="me-inline-editor">
                                        {fieldSchema.type.endsWith('[]') ? renderListEditor(key, fieldSchema) : (
                                            fieldSchema.options ? (
                                                <select
                                                    value={editValue || ''}
                                                    aria-label={label}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    disabled={disabled || isLoading}
                                                    autoFocus
                                                >
                                                    <option value="">Not set</option>
                                                    {fieldSchema.options.map((opt) => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type={fieldSchema.type === 'number' ? 'number' : 'text'}
                                                    value={editValue}
                                                    aria-label={label}
                                                    placeholder={fieldSchema.placeholder}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') { e.preventDefault(); handleSave(key, fieldSchema); }
                                                        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelEditing(); }
                                                    }}
                                                    autoFocus
                                                    disabled={disabled || isLoading}
                                                />
                                            )
                                        )}

                                        {validationError && <p className="me-error">{validationError}</p>}

                                        <div className="me-save-actions">
                                            <button type="button" className="qd-btn qd-btn--primary qd-btn--sm"
                                                    onClick={() => handleSave(key, fieldSchema)} disabled={disabled || isLoading}>
                                                {isLoading ? 'Saving…' : 'Save'}
                                            </button>
                                            <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm"
                                                    onClick={cancelEditing} disabled={isLoading}>
                                                Cancel
                                            </button>
                                            <span className="me-hint">Enter to save · Esc to cancel</span>
                                        </div>
                                    </div>
                                ) : empty ? (
                                    <button
                                        type="button"
                                        className="me-add-value"
                                        onClick={() => startEditing(key, val, fieldSchema)}
                                        disabled={disabled || editingField !== null}
                                    >
                                        Add {label.toLowerCase()}
                                    </button>
                                ) : (
                                    <div className="me-view">
                                        {/* Clicking the value edits it — except on a link,
                                            which should still follow. */}
                                        <div
                                            className="me-display"
                                            onClick={(e) => {
                                                if (disabled || editingField !== null) return;
                                                if (e.target.closest('a')) return;
                                                startEditing(key, val, fieldSchema);
                                            }}
                                        >
                                            {renderValue(val)}
                                        </div>
                                        <div className="me-row-actions">
                                            <button type="button" className="me-icon-btn"
                                                    onClick={() => startEditing(key, val, fieldSchema)}
                                                    title={`Edit ${label}`} aria-label={`Edit ${label}`}
                                                    disabled={disabled || editingField !== null}>
                                                <Pencil />
                                            </button>
                                            {isCustom && onRemove && (
                                                <button type="button" className="me-icon-btn me-delete-icon"
                                                        onClick={() => handleRemove(key)}
                                                        title={`Remove ${label}`} aria-label={`Remove ${label}`}
                                                        disabled={disabled || editingField !== null}>
                                                    <Cross />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </dd>
                        </div>
                    );
                })}
            </dl>

            {readOnlyEntries.length > 0 && (
                <div className="me-readonly">
                    {readOnlyLabel && <p className="me-readonly-label">{readOnlyLabel}</p>}
                    <dl className="me-fields">
                        {readOnlyEntries.map(([key, val]) => (
                            <div key={key} className="me-field me-field--readonly">
                                <dt className="me-key">{humanizeKey(key)}</dt>
                                <dd className="me-val"><span className="me-readonly-value">{String(val)}</span></dd>
                            </div>
                        ))}
                    </dl>
                </div>
            )}

            {/* Add Custom Property Footer */}
            {onSave && (
                <div className="me-footer">
                    {isAddingCustom ? (
                        <div className="me-custom-key-form">
                            <input
                                type="text"
                                placeholder="Property name, e.g. bpm"
                                aria-label="New property name"
                                value={customKeyName}
                                onChange={(e) => setCustomKeyName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') startCustomField();
                                    if (e.key === 'Escape') { e.stopPropagation(); setIsAddingCustom(false); setCustomKeyName(''); }
                                }}
                                autoFocus
                            />
                            <button type="button" className="qd-btn qd-btn--primary qd-btn--sm" onClick={startCustomField}>Add</button>
                            <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm"
                                    onClick={() => { setIsAddingCustom(false); setCustomKeyName(''); }}>Cancel</button>
                        </div>
                    ) : (
                        <button type="button" className="me-add-custom-btn" onClick={() => setIsAddingCustom(true)}
                                disabled={disabled || editingField !== null}>
                            + Add custom property
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default MetadataEditor;
